import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, DialogContent, IconButton, makeStyles, Tooltip } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import FullscreenIcon from '@material-ui/icons/Fullscreen'
import { flattenNoFollow } from '../../../../../../../utils/flatten'
import { useEditor } from './EditorContext'

const useStyles = makeStyles(theme => ({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0
  },
  toolbar: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1),
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  modeToggle: {
    display: 'flex',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    overflow: 'hidden'
  },
  modeBtn: {
    padding: '4px 10px',
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    color: 'var(--color-neutral-600)',
    backgroundColor: 'white',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:not(:last-child)': { borderRight: '1px solid var(--color-neutral-300)' },
    '&:hover': { backgroundColor: 'var(--color-neutral-100)' },
    '&:disabled': { opacity: 0.4, cursor: 'not-allowed' }
  },
  modeBtnActive: {
    backgroundColor: 'var(--color-primary-500)',
    color: 'white',
    '&:hover': { backgroundColor: 'var(--color-primary-600)' }
  },
  spacer: { flex: 1 },
  iconBtn: {
    padding: 4,
    color: 'var(--color-neutral-400)',
    '&:hover': { color: 'var(--color-primary-600)' },
    '&:disabled': { color: 'var(--color-neutral-200)' }
  },
  body: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
    backgroundColor: 'white'
  },
  json: {
    margin: 0,
    padding: theme.spacing(1.5),
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '11px',
    color: 'var(--color-neutral-800)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: 'var(--text-size-sm)',
    fontStyle: 'italic'
  },
  errorBox: {
    padding: theme.spacing(1.5),
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '11px',
    color: 'var(--color-danger-700, #b91c1c)',
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '11px',
    fontFamily: 'var(--font-mono, monospace)'
  },
  th: {
    padding: theme.spacing(0.5, 1),
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-500)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    position: 'sticky',
    top: 0
  },
  td: {
    padding: theme.spacing(0.5, 1),
    borderBottom: '1px solid var(--color-neutral-100)',
    color: 'var(--color-neutral-800)',
    verticalAlign: 'top',
    whiteSpace: 'nowrap'
  },
  cellMuted: {
    color: 'var(--color-neutral-400)',
    fontStyle: 'italic'
  },
  cellEllipsis: {
    color: 'var(--color-neutral-500)',
    cursor: 'help'
  },
  cellTooltipPre: {
    margin: 0,
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '11px',
    maxHeight: 320,
    maxWidth: 480,
    overflow: 'auto',
    whiteSpace: 'pre'
  },
  fieldsWrap: {
    padding: theme.spacing(1.5),
    fontSize: '11px',
    fontFamily: 'var(--font-mono, monospace)'
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    borderBottom: '1px dashed var(--color-neutral-100)'
  },
  fieldAccessor: {
    color: 'var(--color-neutral-800)',
    fontWeight: 600
  },
  fieldType: {
    color: 'var(--color-neutral-500)',
    fontSize: '10px'
  },
  modalPaper: {
    width: '90vw',
    height: '85vh',
    maxWidth: 'none',
    maxHeight: 'none',
    borderRadius: 'var(--border-radius-md, 8px)',
    display: 'flex',
    flexDirection: 'column'
  },
  modalHeader: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.25, 2),
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  modalTitle: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600
  },
  modalContent: {
    flex: 1,
    overflow: 'hidden',
    padding: 0,
    display: 'flex',
    flexDirection: 'column'
  }
}))

// Pipeline executions return `{ data: [...], meta: {...} }`. Treat `result.data`
// as the rows for table mode (mirrors legacy AutoTable behavior). Fall back to
// `result` itself if a caller hands us a raw array.
function extractRows (result) {
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return null
}

function isTableLike (result) {
  const rows = extractRows(result)
  if (!rows || rows.length === 0) return false
  return rows.every(row => row && typeof row === 'object' && !Array.isArray(row))
}

// Mirrors legacy AutoTable: pre-flatten each row with `flattenNoFollow` so
// nested-object paths (e.g. `options.itemStyle.color`) become their own
// columns and arrays stay opaque ("..." cells with full JSON in a tooltip).
function buildFlattenedRows (rows) {
  return rows.map(r => flattenNoFollow(r))
}

function buildColumnsFromFlattened (flatRows) {
  const cols = []
  const seen = new Set()
  for (const row of flatRows) {
    for (const k of Object.keys(row || {})) {
      if (!seen.has(k)) { seen.add(k); cols.push(k) }
    }
  }
  return cols
}

// Mirrors legacy `QueryTableCell` (denali/shared/QueryTableCell.jsx): show
// "<missing>" for undefined, "null" for null, "…" with a JSON tooltip for
// non-primitives, and the raw value otherwise. Kept inline (instead of
// importing the legacy component) so we don't entangle the new editor with
// the legacy component tree.
function TableCellValue ({ value }) {
  const classes = useStyles()
  if (value === undefined) return <span className={classes.cellMuted}>&lt;missing&gt;</span>
  if (value === null) return <span className={classes.cellMuted}>null</span>
  if (typeof value === 'object') {
    return (
      <Tooltip title={<pre className={classes.cellTooltipPre}>{JSON.stringify(value, null, 2)}</pre>} arrow>
        <span className={classes.cellEllipsis}>…</span>
      </Tooltip>
    )
  }
  return <span>{String(value)}</span>
}

TableCellValue.propTypes = {
  value: PropTypes.any
}

function FieldsBody ({ analysis }) {
  const classes = useStyles()
  const fields = analysis?.fields
  if (!fields || fields.length === 0) {
    return <div className={classes.empty}>No analysis yet. Click Run to derive field accessors.</div>
  }
  return (
    <div className={classes.fieldsWrap}>
      {fields.map(field => (
        <div key={field.accessor} className={classes.fieldRow}>
          <span className={classes.fieldAccessor}>{field.accessor}</span>
          <span className={classes.fieldType}>({field.type})</span>
        </div>
      ))}
    </div>
  )
}

FieldsBody.propTypes = {
  analysis: PropTypes.object
}

function ResultsBody ({ result, error, mode, analysis }) {
  const classes = useStyles()
  if (mode === 'fields') return <FieldsBody analysis={analysis} />
  if (error) return <div className={classes.errorBox}>{error}</div>
  if (result == null) {
    return <div className={classes.empty}>No results yet. Click Run to execute the pipeline.</div>
  }
  if (mode === 'table' && isTableLike(result)) {
    const rows = extractRows(result)
    const flatRows = buildFlattenedRows(rows)
    const cols = buildColumnsFromFlattened(flatRows)
    return (
      <table className={classes.table}>
        <thead>
          <tr>{cols.map(c => <th key={c} className={classes.th}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {flatRows.map((row, idx) => (
            <tr key={idx}>
              {cols.map(c => (
                <td key={c} className={classes.td}>
                  <TableCellValue value={row[c]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )
  }
  return <pre className={classes.json}>{JSON.stringify(result, null, 2)}</pre>
}

ResultsBody.propTypes = {
  result: PropTypes.any,
  error: PropTypes.string,
  mode: PropTypes.oneOf(['json', 'table', 'fields']).isRequired,
  analysis: PropTypes.object
}

function ResultsToolbar ({ mode, setMode, tableSupported, fieldsSupported, onPopout, popoutDisabled }) {
  const classes = useStyles()
  return (
    <div className={classes.toolbar}>
      <div className={classes.modeToggle}>
        <button
          type='button'
          className={`${classes.modeBtn} ${mode === 'json' ? classes.modeBtnActive : ''}`}
          onClick={() => setMode('json')}
        >
          json
        </button>
        <button
          type='button'
          className={`${classes.modeBtn} ${mode === 'table' ? classes.modeBtnActive : ''}`}
          onClick={() => setMode('table')}
          disabled={!tableSupported}
        >
          table
        </button>
        <button
          type='button'
          className={`${classes.modeBtn} ${mode === 'fields' ? classes.modeBtnActive : ''}`}
          onClick={() => setMode('fields')}
          disabled={!fieldsSupported}
        >
          fields
        </button>
      </div>
      <div className={classes.spacer} />
      {onPopout && (
        <Tooltip title='Open in modal' arrow>
          <span>
            <IconButton size='small' className={classes.iconBtn} onClick={onPopout} disabled={popoutDisabled}>
              <FullscreenIcon style={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </div>
  )
}

ResultsToolbar.propTypes = {
  mode: PropTypes.oneOf(['json', 'table', 'fields']).isRequired,
  setMode: PropTypes.func.isRequired,
  tableSupported: PropTypes.bool.isRequired,
  fieldsSupported: PropTypes.bool.isRequired,
  onPopout: PropTypes.func,
  popoutDisabled: PropTypes.bool
}

function ResultsView ({ result, error }) {
  const classes = useStyles()
  const { analysis } = useEditor()
  const [mode, setMode] = useState('json')
  const [popped, setPopped] = useState(false)

  const tableSupported = useMemo(() => isTableLike(result), [result])
  const fieldsSupported = !!analysis?.fields?.length

  return (
    <div className={classes.wrap}>
      <ResultsToolbar
        mode={mode}
        setMode={setMode}
        tableSupported={tableSupported}
        fieldsSupported={fieldsSupported}
        onPopout={() => setPopped(true)}
        popoutDisabled={result == null && !error && !fieldsSupported}
      />
      <div className={classes.body}>
        <ResultsBody result={result} error={error} mode={mode} analysis={analysis} />
      </div>
      <Dialog open={popped} onClose={() => setPopped(false)} classes={{ paper: classes.modalPaper }} maxWidth={false}>
        <div className={classes.modalHeader}>
          <span className={classes.modalTitle}>Pipeline Result</span>
          <IconButton size='small' onClick={() => setPopped(false)}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </div>
        <DialogContent className={classes.modalContent}>
          <ResultsToolbar mode={mode} setMode={setMode} tableSupported={tableSupported} fieldsSupported={fieldsSupported} />
          <div className={classes.body}>
            <ResultsBody result={result} error={error} mode={mode} analysis={analysis} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

ResultsView.propTypes = {
  result: PropTypes.any,
  error: PropTypes.string
}

export default ResultsView
